// Device location → working postcode, for "Use my current location" on the
// postcode screen and the Change Location drawer (DEV-51044).
//
// Nothing here applies a location. The caller shows the postcode for the
// engineer to check, and only their confirmation sets it — a position fix on
// an industrial estate can easily land on the neighbouring unit.

import { findNearestPostcode } from './postcodes'

/**
 * Why a lookup produced no postcode.
 *
 * `unavailable` covers both "the device couldn't get a fix" and "it took too
 * long": the engineer can do nothing different about either, so they share a
 * message.
 */
export type DeviceLocationFailure = 'denied' | 'unavailable' | 'no-postcode'

export type DeviceLocationResult =
  | { ok: true; postcode: string; latitude: number; longitude: number }
  | { ok: false; reason: DeviceLocationFailure }

// A phone indoors at a trade counter can take a few seconds to settle. Bound
// the wait so a stalled fix falls through to the "enter a postcode" message
// rather than leaving the button spinning.
const POSITION_TIMEOUT_MS = 10000

// Accept a fix taken in the last minute. An engineer who taps the button twice
// gets the second answer instantly instead of waiting for a fresh fix.
const POSITION_MAX_AGE_MS = 60000

// GeolocationPositionError.PERMISSION_DENIED. Compared numerically rather than
// with `instanceof`, because older Safari exposes the error under a different
// global name and the reference would throw inside our own catch block.
const PERMISSION_DENIED_CODE = 1

/**
 * Whether this browser can report the device position at all.
 *
 * Geolocation requires a secure context, so it is unavailable over plain HTTP
 * (local development). Callers hide the button entirely rather than offering
 * one that can only fail.
 */
export function isDeviceLocationSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'geolocation' in navigator &&
    window.isSecureContext
  )
}

/**
 * Promise wrapper around the callback-based geolocation API.
 *
 * Rejects with the browser's own error object so the caller can map the
 * permission case separately from the rest.
 */
function requestDevicePosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: POSITION_TIMEOUT_MS,
      maximumAge: POSITION_MAX_AGE_MS,
    })
  })
}

/**
 * Ask the device where it is and turn that into the nearest UK postcode.
 *
 * Never throws: every failure comes back as a reason the caller can turn into
 * a message. Returns the coordinates alongside the postcode so a caller can
 * reuse the fix (e.g. the at-branch suggestion in DEV-51045) without asking
 * the device a second time.
 */
export async function locateDevice(): Promise<DeviceLocationResult> {
  if (!isDeviceLocationSupported()) {
    return { ok: false, reason: 'unavailable' }
  }

  let position: GeolocationPosition
  try {
    position = await requestDevicePosition()
  } catch (error) {
    const code = (error as GeolocationPositionError | undefined)?.code
    return {
      ok: false,
      reason: code === PERMISSION_DENIED_CODE ? 'denied' : 'unavailable',
    }
  }

  const { latitude, longitude } = position.coords
  const postcode = await findNearestPostcode(latitude, longitude)
  if (!postcode) {
    return { ok: false, reason: 'no-postcode' }
  }

  return { ok: true, postcode, latitude, longitude }
}
